import { ConsolidatedLocationView } from '@mark43/cad-api';
import { LocationStoreView } from '../../location/types';
import { capitalizeEachWordInString } from '../../common/helpers/typography';
import {
    formatFullAddress,
    getCrossStreetsString,
    getLocationSummarySubpremises,
} from './location-display';

const formatPlacename = (location?: ConsolidatedLocationView | LocationStoreView) => {
    const placeName = location?.placeName;

    if (!placeName) {
        return;
    }

    if (location?.resolverSource === 'GEO_ALIAS') {
        return placeName;
    }

    return capitalizeEachWordInString(placeName);
};

export function getLocationSummary(
    location?: ConsolidatedLocationView | LocationStoreView,
    showCrossStreets?: boolean
) {
    if (!location) {
        return {
            firstLine: '',
            secondLine: '',
            thirdLine: '',
            fourthLine: '',
        };
    }
    const isForced = location.source === 'FORCED_LOCATION';
    const isForcedSubpremiseResult =
        ('isForcedSubpremiseResult' in location && location.isForcedSubpremiseResult) ||
        location.resolverSource === 'FORCED_SUB_PREMISES';

    const placeName = formatPlacename(location);

    let firstLine = placeName;
    let secondLine;

    if (!placeName) {
        firstLine = location.streetAddress
            ? capitalizeEachWordInString(
                  isForced ? location.streetAddress.split('#')[0].trim() : location.streetAddress
              )
            : '';
        secondLine = formatFullAddress(location, {
            omitPlacename: true,
            omitAddress: true,
        });
    } else {
        secondLine = formatFullAddress(location, {
            omitPlacename: true,
        });
    }

    if (location.resolverSource === 'LAT_LONG') {
        firstLine = `${firstLine} (Verified)`;
    }

    return {
        firstLine,
        secondLine,
        thirdLine: isForcedSubpremiseResult
            ? `[FORCED] ${getLocationSummarySubpremises(location)}`
            : getLocationSummarySubpremises(location),
        fourthLine: showCrossStreets ? getCrossStreetsString(location, true) : undefined,
    };
}

export function getForcedResult(location: ConsolidatedLocationView | LocationStoreView) {
    const forcedLocationInfo = [
        capitalizeEachWordInString(location.streetAddress?.split('#')?.[0]?.trim() ?? ''),
        getLocationSummarySubpremises(location),
    ].filter((s) => !!s);
    return `[FORCED] ${forcedLocationInfo.join(', ')}`;
}

export function getSingleLineFullAddressLocationSummary(
    locationSummary: ReturnType<typeof getLocationSummary>,
    placeName?: string
): ReturnType<typeof getLocationSummary> {
    // Full address on single line, move everything one line up
    const fullAddress = `${locationSummary.firstLine}, ${locationSummary.secondLine}`;
    return {
        firstLine: placeName || fullAddress,
        secondLine: placeName ? locationSummary.secondLine : locationSummary.thirdLine,
        thirdLine: placeName ? locationSummary.thirdLine : locationSummary.fourthLine ?? '',
        fourthLine: undefined,
    };
}
